import React, { useState } from 'react';

import cn from 'classnames';

import { formatDecimalInput } from '@shared/helpers';
import {
    CustomModal,
    DateInput,
    Editable,
    Icon,
    Input,
    Popup,
    TextArea,
    UiButton,
    UiText,
} from '@shared/ui';

import cls from './EquipmentInventoryModal.module.css';
import { HistoryLogs } from './HistoryLogs';
import { InventoryMainImage } from './InventoryMainImage/InventoryMainImage';
import { InventoryPhotosDropzone } from './InventoryPhotosDropzone';
import { normalizeDecimal } from '../lib/helpers';
import { ModalMode } from '../model/types';
import { useEquipmentInventoryModal } from '../model/useEquipmentInventoryModal';

const formatDecimalPreview = (value: string) => {
    const normalized = value.trim().replace(/\s/g, '').replace(',', '.');

    if (!normalized) {
        return '0';
    }

    const parsed = Number(normalized);

    if (!Number.isFinite(parsed)) {
        return value;
    }

    const rounded = Math.round((parsed + Number.EPSILON) * 100) / 100;

    return String(rounded).replace('.', ',');
};

interface EquipmentInventoryModalProps {
    isOpen: boolean;
    mode: ModalMode;
    inventoryGuid: string | null;
    warehouseId: number;
    warehouseTitle: string;
    hasFullAccess: boolean;
    currencySign: string;
    onClose: () => void;
    onAfterClose: () => void;
}

export const EquipmentInventoryModal = ({
    isOpen,
    mode,
    inventoryGuid,
    warehouseId,
    warehouseTitle,
    hasFullAccess,
    currencySign,
    onClose,
    onAfterClose,
}: EquipmentInventoryModalProps) => {
    const [isDeletePopupOpen, setIsDeletePopupOpen] = useState(false);
    const [isAmountFocused, setIsAmountFocused] = useState(false);
    const [isPriceFocused, setIsPriceFocused] = useState(false);
    const [isCostFocused, setIsCostFocused] = useState(false);
    const {
        t,
        inventoryStatusesData,
        imageError,
        dateInputError,
        setPurchaseDateInputValue,
        setFreeDateInputValue,
        purchaseDateOptions,
        freeDateOptions,
        saveField,
        saveStatus,
        uploadPhotos,
        removeInventory,
        inventoryQuery,
        form,
        setForm,
        inventory,
        savePurchaseDate,
        saveFreeDate,
        removeInventoryMutation,
        uploadPhotosMutation,
    } = useEquipmentInventoryModal(isOpen, inventoryGuid, warehouseId, onClose);

    const handleAfterClose = () => {
        setIsDeletePopupOpen(false);
        onAfterClose();
    };

    const handleRemoveInventory = () => {
        setIsDeletePopupOpen(false);
        removeInventory();
    };

    return (
        <CustomModal
            isOpen={isOpen}
            onHideModal={onClose}
            onAfterClose={handleAfterClose}
            contentClasses={cls.content}
            isLoading={inventoryQuery.isLoading}
        >
            <CustomModal.Header classes={cls.header}>
                <InventoryMainImage
                    previewUrl={form.previewUrl}
                    error={imageError}
                    onUpload={uploadPhotos}
                />
                <div className={cls.titleBlock}>
                    <Editable
                        autoFocus={mode === 'copy'}
                        disabled={!hasFullAccess}
                        value={form.title}
                        maxLength={255}
                        trimOnSubmit
                        submitOnlyIfChanged
                        className={cls.title}
                        inputClassName={cls.titleInput}
                        inputProps={
                            {
                                'data-id': 'inventory-title',
                            } as React.InputHTMLAttributes<HTMLInputElement>
                        }
                        onSubmit={async (nextTitle) => {
                            if (!nextTitle || !inventoryGuid) {
                                return;
                            }

                            setForm((previous) => ({ ...previous, title: nextTitle }));
                            saveField('title', 'title', nextTitle, { title: nextTitle });
                        }}
                    >
                        {form.title}
                    </Editable>

                    {inventory && (
                        <div className={cls.inputsWrapper}>
                            <div className={cls.inputsRow}>
                                <Input
                                    labelClassName={cls.label}
                                    label={t('SettingsBlock.InventoryNumber.labelText')}
                                    maxLength={255}
                                    value={form.inventoryNumber}
                                    disabled={!hasFullAccess}
                                    data-id="inventory-number"
                                    onFocus={(event) => event.target.select()}
                                    onChange={(event) =>
                                        setForm((previous) => ({
                                            ...previous,
                                            inventoryNumber: event.target.value,
                                        }))
                                    }
                                    onBlur={() => {
                                        const inventoryNumber = form.inventoryNumber.trim();
                                        setForm((previous) => ({ ...previous, inventoryNumber }));
                                        saveField(
                                            'inventoryNumber',
                                            'inventory_number',
                                            inventoryNumber
                                        );
                                    }}
                                />
                                <DateInput
                                    inputValue={form.purchaseDate}
                                    parameterName="purchase_date"
                                    options={purchaseDateOptions}
                                    error={dateInputError}
                                    title={t('SettingsBlock.dateInput.labelText')}
                                    isDisabled={!hasFullAccess}
                                    onDateChange={(event) => {
                                        const nextDate = event.target.value;
                                        setPurchaseDateInputValue(nextDate);

                                        if (nextDate.length === 10 || nextDate === '') {
                                            savePurchaseDate(nextDate);
                                        }
                                    }}
                                    setInputValue={setPurchaseDateInputValue}
                                    callback={(date = '') => savePurchaseDate(date)}
                                />
                            </div>
                            <div className={cls.inputsRow}>
                                <div className={cls.amount}>
                                    <Input
                                        label={t('SettingsBlock.AmountAndUnit.labelText')}
                                        value={
                                            isAmountFocused
                                                ? form.amount
                                                : formatDecimalPreview(form.amount)
                                        }
                                        disabled={!hasFullAccess}
                                        labelClassName={cls.label}
                                        inputClassName={cls.input}
                                        data-id="inventory-amount"
                                        onFocus={(event) => {
                                            setIsAmountFocused(true);
                                            event.target.select();
                                        }}
                                        onChange={(event) =>
                                            setForm((previous) => ({
                                                ...previous,
                                                amount: formatDecimalInput(event.target.value),
                                            }))
                                        }
                                        onBlur={() => {
                                            setIsAmountFocused(false);
                                            const amount = normalizeDecimal(form.amount, '1');
                                            setForm((previous) => ({
                                                ...previous,
                                                amount,
                                            }));
                                            saveField('amount', 'amount', amount, {
                                                amount: Number(amount.replace(',', '.')),
                                            });
                                        }}
                                    />
                                    <Input
                                        maxLength={100}
                                        value={form.unit}
                                        disabled={!hasFullAccess}
                                        inputClassName={cls.input}
                                        data-id="inventory-unit"
                                        onFocus={(event) => event.target.select()}
                                        onChange={(event) =>
                                            setForm((previous) => ({
                                                ...previous,
                                                unit: event.target.value,
                                            }))
                                        }
                                        onBlur={() => {
                                            const unit =
                                                form.unit.trim() || t('Unit.emptyValue.text');
                                            setForm((previous) => ({ ...previous, unit }));
                                            saveField('unit', 'unit', unit);
                                        }}
                                    />
                                </div>
                                <Input
                                    label={`${t('SettingsBlock.Price.labelText')} ${currencySign}`}
                                    labelClassName={cls.label}
                                    value={
                                        isPriceFocused
                                            ? form.price
                                            : formatDecimalPreview(form.price)
                                    }
                                    disabled={!hasFullAccess}
                                    inputClassName={cls.input}
                                    data-id="inventory-price"
                                    onFocus={(event) => {
                                        setIsPriceFocused(true);
                                        event.target.select();
                                    }}
                                    onChange={(event) =>
                                        setForm((previous) => ({
                                            ...previous,
                                            price: formatDecimalInput(event.target.value),
                                        }))
                                    }
                                    onBlur={() => {
                                        setIsPriceFocused(false);
                                        const price = normalizeDecimal(form.price);
                                        setForm((previous) => ({ ...previous, price }));
                                        saveField('price', 'price', price);
                                    }}
                                />
                                <Input
                                    labelClassName={cls.label}
                                    label={`${t('SettingsBlock.Cost.labelText')}, ${currencySign}`}
                                    value={
                                        isCostFocused ? form.cost : formatDecimalPreview(form.cost)
                                    }
                                    disabled={!hasFullAccess}
                                    inputClassName={cls.input}
                                    data-id="inventory-cost"
                                    onFocus={(event) => {
                                        setIsCostFocused(true);
                                        event.target.select();
                                    }}
                                    onChange={(event) =>
                                        setForm((previous) => ({
                                            ...previous,
                                            cost: formatDecimalInput(event.target.value),
                                        }))
                                    }
                                    onBlur={() => {
                                        setIsCostFocused(false);
                                        const cost = normalizeDecimal(form.cost);
                                        setForm((previous) => ({ ...previous, cost }));
                                        saveField('cost', 'cost', cost);
                                    }}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </CustomModal.Header>

            <CustomModal.Body classes={cls.body}>
                <div className={cls.sectionRow}>
                    <UiText weight="bold" variant="boldPlain" tag="h4">
                        {t('EditInventoryModal.return.location')}
                    </UiText>
                    <UiText variant="subPlain">{warehouseTitle}</UiText>
                </div>
                {!inventory && !inventoryQuery.isLoading && (
                    <UiText className={cls.loaderText} variant="subPlain">
                        {t('Common.error.text')}
                    </UiText>
                )}
                {inventory && (
                    <>
                        <div className={cls.sectionRow}>
                            <UiText weight="bold" variant="boldPlain" tag="h4">
                                {t('Status.return.statusWrapper.subTitle')}
                            </UiText>
                            <div className={cls.sectionContent}>
                                <ul className={cls.statusList}>
                                    {inventoryStatusesData.map((statusItem) => (
                                        <li key={statusItem.status}>
                                            <button
                                                type="button"
                                                className={cn(cls.statusButton, {
                                                    [cls.statusButtonActive]:
                                                        form.status === statusItem.status,
                                                })}
                                                style={
                                                    form.status === statusItem.status
                                                        ? { backgroundColor: statusItem.color }
                                                        : { color: statusItem.color }
                                                }
                                                onClick={() => saveStatus(statusItem.status)}
                                                data-id="status-button"
                                                data-status={statusItem.status}
                                            >
                                                <Icon
                                                    fill="currentColor"
                                                    iconName={statusItem.icon}
                                                />
                                                {statusItem.text}
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>

                        {form.status === 0 && (
                            <div className={cls.sectionRow}>
                                <UiText weight="bold" variant="boldPlain" tag="h4">
                                    {t('Status.return.isInWorkMode.subTitle')}
                                </UiText>
                                <div className={cn(cls.sectionContent, cls.beFreeContent)}>
                                    <DateInput
                                        inputValue={form.freeDate}
                                        parameterName="free_date"
                                        options={freeDateOptions}
                                        error={dateInputError}
                                        onDateChange={(event) => {
                                            const nextDate = event.target.value;
                                            setFreeDateInputValue(nextDate);

                                            if (nextDate.length === 10 || nextDate === '') {
                                                saveFreeDate(nextDate);
                                            }
                                        }}
                                        setInputValue={setFreeDateInputValue}
                                        callback={(date = '') => {
                                            setFreeDateInputValue(date);
                                            saveFreeDate(date);
                                        }}
                                    />
                                </div>
                            </div>
                        )}

                        {form.status === 3 && hasFullAccess && (
                            <div className={cls.sectionRow}>
                                <div aria-hidden="true" />
                                <div className={cn(cls.sectionContent, cls.statusActionList)}>
                                    <UiButton
                                        className={cls.removeButton}
                                        btnColor="danger"
                                        btnText={t('Status.removeBtn.text')}
                                        disabled={removeInventoryMutation.isPending}
                                        onClick={() => setIsDeletePopupOpen(true)}
                                        data-id="remove-inventory-button"
                                    />
                                </div>
                            </div>
                        )}

                        <div className={cls.fieldWide}>
                            <TextArea
                                inputName="description"
                                label={t('Description.return.subTitle')}
                                value={form.description}
                                maxLength={1000}
                                labelClassName={cls.label}
                                textareaClassName={cls.textarea}
                                onChange={(description) =>
                                    setForm((previous) => ({
                                        ...previous,
                                        description,
                                    }))
                                }
                                onBlur={() => {
                                    const description = form.description.trim();
                                    setForm((previous) => ({
                                        ...previous,
                                        description,
                                    }));
                                    saveField('description', 'description', description);
                                }}
                            />
                        </div>

                        <InventoryPhotosDropzone
                            error={imageError}
                            isDisabled={uploadPhotosMutation.isPending}
                            onUpload={uploadPhotos}
                        />

                        <HistoryLogs inventoryGuid={inventoryGuid} isOpen={isOpen} />
                    </>
                )}
            </CustomModal.Body>
            <Popup
                isOpen={isDeletePopupOpen}
                type="delete"
                contentText={t('Inventory.deleteInventory.text')}
                deleteButtonText={t('Inventory.deleteInventory.deleteBtn.text')}
                cancelButtonText={t('Inventory.deleteInventory.cancelBtn.text')}
                onHidePopup={() => setIsDeletePopupOpen(false)}
                btnActionCb={handleRemoveInventory}
                isPending={removeInventoryMutation.isPending}
            />
        </CustomModal>
    );
};
